import React, { useState } from 'react';
import { ArrowRight, Eye, TrendingUp, MapPin } from 'lucide-react';
import PanneauxModal from './PanneauxModal';
import QuoteModal from './QuoteModal';

const Hero = () => {
  const [isPanneauxModalOpen, setIsPanneauxModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
      {/* Background with overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-cyan-600 opacity-95"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="text-white space-y-8">
            <div className="inline-block">
              <span className="bg-cyan-400/20 backdrop-blur-sm text-cyan-100 px-4 py-2 rounded-full text-sm font-medium">
                Leader en publicité extérieure
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
              Donnez de la <span className="text-cyan-300">visibilité</span> à votre marque
            </h1>

            <p className="text-xl text-cyan-50 leading-relaxed">
              Installation et gestion d'espaces publicitaires premium en Afrique centrale et de l'ouest. 
              Des solutions sur mesure pour maximiser l'impact de vos campagnes.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection('services')}
                className="bg-white text-primary px-8 py-4 rounded-lg hover:bg-cyan-50 transition-all hover:shadow-xl font-semibold flex items-center gap-2 group"
              >
                Découvrir nos services
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </button>
              <button
                onClick={() => setIsPanneauxModalOpen(true)}
                className="bg-cyan-500 text-white px-8 py-4 rounded-lg hover:bg-cyan-600 transition-all hover:shadow-xl font-semibold flex items-center gap-2 group"
              >
                <MapPin size={20} aria-hidden="true" />
                Nos Panneaux
              </button>
              <button
                onClick={() => setIsQuoteModalOpen(true)}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white/10 transition-all font-semibold"
                data-testid="hero-quote-button"
              >
                Demander un devis
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Eye size={24} className="text-cyan-300" aria-hidden="true" />
                </div>
                <div className="text-3xl font-bold">100+</div>
                <div className="text-cyan-100 text-sm">Panneaux installés</div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <TrendingUp size={24} className="text-cyan-300" aria-hidden="true" />
                </div>
                <div className="text-3xl font-bold">200+</div>
                <div className="text-cyan-100 text-sm">Clients satisfaits</div>
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold">3</div>
                <div className="text-cyan-100 text-sm">Pays d'Afrique</div>
              </div>
            </div>
          </div>

          {/* Right content - Image showcase */}
          <div className="relative lg:block hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-400/30 to-transparent rounded-3xl blur-2xl"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_crm-showcase-5/artifacts/8r64ok04_D59C4209-92E8-4E07-A19E-ACE030AC42A4.png"
                alt="Panneaux publicitaires SESAM avec affichages LED modernes"
                className="relative rounded-3xl shadow-2xl w-full h-auto transform hover:scale-105 transition-transform duration-500"
              />
            </div>
            
            {/* Floating card */}
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 text-green-600 p-3 rounded-xl">
                  <TrendingUp size={24} aria-hidden="true" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-800">+85%</div>
                  <div className="text-sm text-gray-600">Visibilité accrue</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
        </svg>
      </div>

      {/* Panneaux Modal */}
      <PanneauxModal 
        isOpen={isPanneauxModalOpen} 
        onClose={() => setIsPanneauxModalOpen(false)} 
      />

      {/* Quote Modal */}
      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </section>
  );
};

export default Hero;
